(function (globalScope) {
  'use strict';

  const TRACKING_KEYS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
    'gclid',
    'fbclid',
    'msclkid'
  ];

  function extractTrackingParams(search) {
    const pageParams = new URLSearchParams(search || '');
    return TRACKING_KEYS.reduce((result, key) => {
      const value = pageParams.get(key);
      if (value) result[key] = value;
      return result;
    }, {});
  }

  function appendTrackingParams(href, search, origin) {
    if (!href || href.charAt(0) === '#') return href;

    try {
      const pageParams = new URLSearchParams(search || '');
      const target = new URL(href, origin);
      const siteOrigin = new URL(origin).origin;

      if (target.origin !== siteOrigin) return href;

      TRACKING_KEYS.forEach((key) => {
        const value = pageParams.get(key);
        if (value && !target.searchParams.has(key)) {
          target.searchParams.set(key, value);
        }
      });

      const isAbsolute = /^[a-z][a-z0-9+.-]*:/i.test(href);
      return isAbsolute
        ? target.toString()
        : `${target.pathname}${target.search}${target.hash}`;
    } catch (_error) {
      return href;
    }
  }

  function findVariant(variants, selectedOptions) {
    if (!Array.isArray(variants) || !Array.isArray(selectedOptions)) return null;
    return variants.find((variant) => (
      Array.isArray(variant.options)
      && variant.options.length === selectedOptions.length
      && variant.options.every((option, index) => option === selectedOptions[index])
    )) || null;
  }

  function setHiddenTrackingValues(root, params) {
    root.querySelectorAll('[data-tracking-key]').forEach((input) => {
      const key = input.dataset.trackingKey;
      input.value = params[key] || '';
    });
  }

  function initTracking(root) {
    const params = extractTrackingParams(globalScope.location.search);
    setHiddenTrackingValues(root, params);

    root.querySelectorAll('a[data-campaign-link]').forEach((link) => {
      const href = link.getAttribute('href');
      link.setAttribute(
        'href',
        appendTrackingParams(href, globalScope.location.search, globalScope.location.origin)
      );
    });
  }

  function initGallery(root) {
    const mainImage = root.querySelector('.fg20k__gallery-main-image');
    const buttons = Array.from(root.querySelectorAll('[data-gallery-button]'));
    if (!mainImage || buttons.length < 2) return;

    buttons.forEach((button) => {
      button.addEventListener('click', () => {
        const nextSource = button.dataset.gallerySrc;
        if (!nextSource || button.classList.contains('is-active')) return;

        buttons.forEach((item) => {
          const active = item === button;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-pressed', active ? 'true' : 'false');
        });

        mainImage.classList.add('is-changing');
        mainImage.removeAttribute('srcset');
        mainImage.removeAttribute('sizes');
        mainImage.src = nextSource;
        mainImage.alt = button.dataset.galleryAlt || '';
        globalScope.requestAnimationFrame(() => {
          mainImage.classList.remove('is-changing');
        });
      });
    });
  }

  function updateVariantUI(root, variant) {
    const hiddenVariant = root.querySelector('[data-variant-id]');
    const currentPrice = root.querySelector('[data-current-price]');
    const addPrice = root.querySelector('[data-atc-price]');
    const stickyPrice = root.querySelector('[data-sticky-price]');
    const comparePrice = root.querySelector('[data-compare-price]');
    const saving = root.querySelector('[data-saving]');
    const addButton = root.querySelector('[data-add-to-cart]');
    const addLabel = root.querySelector('[data-atc-label]');
    const stickyButton = root.querySelector('[data-sticky-submit]');
    const stickyLabel = root.querySelector('[data-sticky-label]');

    if (!variant) {
      if (hiddenVariant) hiddenVariant.value = '';
      if (currentPrice) currentPrice.textContent = '—';
      if (addPrice) addPrice.textContent = '—';
      if (stickyPrice) stickyPrice.textContent = '—';
      if (comparePrice) {
        comparePrice.hidden = true;
        comparePrice.textContent = '';
      }
      if (saving) {
        saving.hidden = true;
        saving.textContent = '';
      }
      if (addButton) addButton.disabled = true;
      if (addLabel) addLabel.textContent = 'Configuration unavailable';
      if (stickyButton) stickyButton.disabled = true;
      if (stickyLabel) stickyLabel.textContent = 'Unavailable';
      return;
    }

    if (hiddenVariant) hiddenVariant.value = variant.id;
    if (currentPrice) currentPrice.textContent = variant.price;
    if (addPrice) addPrice.textContent = variant.price;
    if (stickyPrice) stickyPrice.textContent = variant.price;

    if (comparePrice) {
      comparePrice.hidden = !variant.comparePrice;
      comparePrice.textContent = variant.comparePrice || '';
    }
    if (saving) {
      saving.hidden = !variant.saving;
      saving.textContent = variant.saving ? `Save ${variant.saving}` : '';
    }

    if (addButton) addButton.disabled = !variant.available;
    if (addLabel) {
      addLabel.textContent = variant.available
        ? root.dataset.atcLabel
        : 'Pilot unit unavailable';
    }
    if (stickyButton) stickyButton.disabled = !variant.available;
    if (stickyLabel) {
      stickyLabel.textContent = variant.available
        ? root.dataset.atcLabel
        : 'Unavailable';
    }

    if (globalScope.history && globalScope.history.replaceState) {
      const url = new URL(globalScope.location.href);
      url.searchParams.set('variant', variant.id);
      globalScope.history.replaceState({}, '', url.toString());
    }
  }

  function initVariants(root) {
    const variantsNode = root.querySelector('[data-variant-json]');
    const selects = Array.from(root.querySelectorAll('[data-option-select]'));
    if (!variantsNode || !selects.length) return;

    let variants = [];
    try {
      variants = JSON.parse(variantsNode.textContent);
    } catch (_error) {
      return;
    }

    const update = () => {
      const selectedOptions = selects.map((select) => select.value);
      updateVariantUI(root, findVariant(variants, selectedOptions));
    };

    selects.forEach((select) => select.addEventListener('change', update));
    update();
  }

  function initScrollTargets(root) {
    root.querySelectorAll('[data-scroll-target]').forEach((button) => {
      button.addEventListener('click', () => {
        const targetId = button.dataset.scrollTarget;
        const target = targetId ? document.getElementById(targetId) : null;
        if (!target) return;

        target.scrollIntoView({
          behavior: globalScope.matchMedia('(prefers-reduced-motion: reduce)').matches
            ? 'auto'
            : 'smooth',
          block: 'start'
        });
      });
    });
  }

  function initSticky(root) {
    const sticky = root.querySelector('[data-sticky]');
    const actionZone = root.querySelector('[data-primary-action-zone]');
    const stickySubmit = root.querySelector('[data-sticky-submit]');
    const addButton = root.querySelector('[data-add-to-cart]');

    if (stickySubmit && addButton) {
      stickySubmit.addEventListener('click', () => addButton.click());
    }

    if (!sticky) return;
    if (!actionZone || !('IntersectionObserver' in globalScope)) {
      sticky.hidden = false;
      return;
    }

    const observer = new IntersectionObserver(([entry]) => {
      sticky.hidden = entry.isIntersecting;
    }, { threshold: 0.12 });
    observer.observe(actionZone);
  }

  function initSuccessMessage(root) {
    const success = root.querySelector('[data-form-success]');
    if (!success) return;
    success.focus({ preventScroll: true });
    success.scrollIntoView({ block: 'center' });
  }

  function initRoot(root) {
    if (!root || root.dataset.pilotReady === 'true') return;
    root.dataset.pilotReady = 'true';
    root.dataset.atcLabel = root.dataset.atcLabel || 'Claim a pilot unit';

    initTracking(root);
    initGallery(root);
    initVariants(root);
    initScrollTargets(root);
    initSticky(root);
    initSuccessMessage(root);
  }

  function boot(scope) {
    const container = scope && scope.querySelectorAll ? scope : document;
    container.querySelectorAll('[data-compact-heater-pilot]').forEach(initRoot);
  }

  const api = {
    appendTrackingParams,
    extractTrackingParams,
    findVariant,
    updateVariantUI,
    initRoot
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }

  if (globalScope) {
    globalScope.FogattiCompactHeaterPilot = api;
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => boot(document), { once: true });
    } else {
      boot(document);
    }
    document.addEventListener('shopify:section:load', (event) => boot(event.target));
  }
})(typeof window !== 'undefined' ? window : null);
